import MapboxDraw from '@mapbox/mapbox-gl-draw'

type FreehandState = {
  polygon: any // Draw feature
  currentVertex: number
  dragging: boolean
  drawing: boolean
  mouseDownPixel?: { x: number; y: number }
  mouseDownLngLat?: [number, number]
  lastAddedPixel?: { x: number; y: number }
  firstLngLat?: [number, number]
  lastClickTime?: number
}

function pxTolForZoom(z: number) {
  // Aggressive tolerance for very jagged lines with minimal vertices
  if (z < 8) return 30
  if (z < 12) return 25
  if (z < 15) return 20
  return 15
}

const DRAG_START_PX = 5

function disableMapInteractions(ctx: any) {
  ctx.map.getCanvas().style.cursor = 'crosshair'
  ctx.map.dragPan.disable()
  ctx.map.doubleClickZoom.disable()
}

function enableMapInteractions(ctx: any) {
  ctx.map.getCanvas().style.cursor = ''
  ctx.map.dragPan.enable()
  ctx.map.doubleClickZoom.enable()
}

function startFeatureAt(
  ctx: any,
  state: FreehandState,
  lng: number,
  lat: number
) {
  state.polygon = ctx.newFeature({
    type: MapboxDraw.constants.geojsonTypes.FEATURE,
    properties: { isFreehand: true },
    geometry: {
      type: MapboxDraw.constants.geojsonTypes.POLYGON,
      coordinates: [[]],
    },
  })
  ctx.addFeature(state.polygon)

  state.firstLngLat = [lng, lat]
  state.polygon.updateCoordinate('0.0', lng, lat)
  state.currentVertex = 0
  state.lastAddedPixel = ctx.map.project({ lng, lat })
}

// Simple coordinate simplification function
function simplifyCoords(
  coords: [number, number][],
  tolerance: number
): [number, number][] {
  if (coords.length <= 3) return coords

  const simplified: [number, number][] = [coords[0]]

  for (let i = 1; i < coords.length - 1; i++) {
    const prev = simplified[simplified.length - 1]
    const curr = coords[i]

    // Calculate distance between points
    const dx = curr[0] - prev[0]
    const dy = curr[1] - prev[1]
    const dist = Math.sqrt(dx * dx + dy * dy)

    // Only add point if it's far enough from the last one
    if (dist > tolerance) {
      simplified.push(curr)
    }
  }

  // Always include the last point (which should be same as first for closed polygon)
  simplified.push(coords[coords.length - 1])

  return simplified
}

// Function to remove only self-intersections while preserving original shape
function createValidPolygon(coords: [number, number][]): [number, number][] {
  if (coords.length < 4) return coords

  // Remove duplicate consecutive points
  const cleaned: [number, number][] = [coords[0]]
  for (let i = 1; i < coords.length; i++) {
    const prev = cleaned[cleaned.length - 1]
    const curr = coords[i]
    if (prev[0] !== curr[0] || prev[1] !== curr[1]) {
      cleaned.push(curr)
    }
  }

  // If we have too few points after cleaning, return original
  if (cleaned.length < 4) return coords

  // Remove self-intersections while preserving the original shape
  return removeSelfIntersections(cleaned)
}

// Remove self-intersections by finding and removing intersecting segments
function removeSelfIntersections(
  coords: [number, number][]
): [number, number][] {
  if (coords.length < 4) return coords

  // Find all intersections first
  const intersections: Array<{
    segmentA: number
    segmentB: number
    point: [number, number]
  }> = []

  for (let i = 0; i < coords.length - 1; i++) {
    for (let j = i + 2; j < coords.length - 1; j++) {
      // Don't check adjacent segments or last segment with first (unless it's a real intersection)
      if (j === coords.length - 2 && i === 0) continue

      const intersection = getLineIntersection(
        coords[i],
        coords[i + 1],
        coords[j],
        coords[j + 1]
      )

      if (intersection) {
        intersections.push({
          segmentA: i,
          segmentB: j,
          point: intersection,
        })
      }
    }
  }

  // If no intersections, return original
  if (intersections.length === 0) {
    return coords
  }

  // Sort intersections by their position along the path (earliest first)
  intersections.sort(
    (a, b) =>
      Math.min(a.segmentA, a.segmentB) - Math.min(b.segmentA, b.segmentB)
  )

  // Handle the first intersection by removing the loop
  const firstIntersection = intersections[0]
  const startSeg = Math.min(
    firstIntersection.segmentA,
    firstIntersection.segmentB
  )
  const endSeg = Math.max(
    firstIntersection.segmentA,
    firstIntersection.segmentB
  )

  const result: [number, number][] = []

  // Keep coordinates up to the start of the intersection
  for (let i = 0; i <= startSeg; i++) {
    result.push(coords[i])
  }

  // Add the intersection point
  result.push(firstIntersection.point)

  // Skip the loop and continue from after the end segment
  for (let i = endSeg + 1; i < coords.length; i++) {
    result.push(coords[i])
  }

  // If we still have intersections after this fix, recursively clean
  if (result.length >= 4) {
    const stillHasIntersections = hasIntersections(result)
    if (stillHasIntersections) {
      return removeSelfIntersections(result)
    }
  }

  return result
}

// Helper function to check if a polygon still has intersections
function hasIntersections(coords: [number, number][]): boolean {
  for (let i = 0; i < coords.length - 1; i++) {
    for (let j = i + 2; j < coords.length - 1; j++) {
      if (j === coords.length - 2 && i === 0) continue

      const intersection = getLineIntersection(
        coords[i],
        coords[i + 1],
        coords[j],
        coords[j + 1]
      )

      if (intersection) {
        return true
      }
    }
  }
  return false
}

// Check if two line segments intersect and return intersection point
function getLineIntersection(
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  p4: [number, number]
): [number, number] | null {
  const x1 = p1[0],
    y1 = p1[1]
  const x2 = p2[0],
    y2 = p2[1]
  const x3 = p3[0],
    y3 = p3[1]
  const x4 = p4[0],
    y4 = p4[1]

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)

  if (Math.abs(denom) < 1e-10) {
    return null // Lines are parallel
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom

  // Check if intersection is within both line segments
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)]
  }

  return null
}

const DrawFreehandPolygon = {
  onSetup(this: any): FreehandState {
    this.clearSelectedFeatures()
    this.updateUIClasses({ mouse: MapboxDraw.constants.cursors.ADD })
    this.activateUIButton('polygon')
    this.setActionableState({ trash: true })

    return {
      polygon: null,
      currentVertex: 0,
      dragging: false,
      drawing: false,
      mouseDownPixel: undefined,
      mouseDownLngLat: undefined,
      lastAddedPixel: undefined,
      firstLngLat: undefined,
    }
  },

  onClick(this: any, state: FreehandState, e: any) {
    // If a drag is ongoing, let mouseup finish it
    if (state.dragging) return

    // If not yet drawing, start click-to-draw mode
    if (!state.drawing && !state.polygon) {
      const { lng, lat } = e.lngLat

      state.drawing = true
      disableMapInteractions(this)
      startFeatureAt(this, state, lng, lat)
      // prime double-click detection window
      state.lastClickTime = Date.now()
      return
    }

    // If already drawing, support double-click via click timing as well
    if (state.drawing && state.polygon) {
      const now = Date.now()
      if (state.lastClickTime && now - state.lastClickTime < 350) {
        this.finishClickDrawing(state)
      } else {
        state.lastClickTime = now
      }
    }
  },

  finishDrawing(this: any, state: FreehandState) {
    if (!state.polygon || !state.dragging) return

    // Stop drawing (drag mode)
    state.dragging = false
    enableMapInteractions(this)
    state.mouseDownPixel = undefined
    state.mouseDownLngLat = undefined

    this.completePolygon(state)
  },

  finishClickDrawing(this: any, state: FreehandState) {
    if (!state.polygon || !state.drawing) return

    // Stop drawing (click mode)
    state.drawing = false
    enableMapInteractions(this)

    this.completePolygon(state)
  },

  // Shared polygon completion used by both drag and click modes
  completePolygon(this: any, state: FreehandState) {
    // Ensure we have enough points for a valid polygon
    if (state.currentVertex < 2) {
      this.deleteFeature([state.polygon.id], { silent: true })
      this.changeMode('simple_select')
      return
    }

    // Close the polygon by connecting back to the first point
    if (state.firstLngLat) {
      const next = state.currentVertex + 1
      state.polygon.updateCoordinate(
        `0.${next}`,
        state.firstLngLat[0],
        state.firstLngLat[1]
      )
    }

    // Get the current coordinates and create a valid polygon
    const geojson = state.polygon.toGeoJSON()
    let coords: [number, number][] = geojson.geometry.coordinates[0]

    // First simplify to reduce points
    if (coords.length > 4) {
      const tolerance = 0.0001 // degrees
      coords = simplifyCoords(coords, tolerance)
    }

    // Then create a valid polygon without self-intersections
    if (coords.length >= 4) {
      const validCoords = createValidPolygon(coords)

      // Make sure the polygon is properly closed
      if (
        validCoords.length >= 4 &&
        (validCoords[0][0] !== validCoords[validCoords.length - 1][0] ||
          validCoords[0][1] !== validCoords[validCoords.length - 1][1])
      ) {
        validCoords.push([validCoords[0][0], validCoords[0][1]])
      }

      if (validCoords.length >= 4) {
        state.polygon.setCoordinates([validCoords])
      }
    }

    // Fire the create event and switch to select mode
    this.map.fire('draw.create', {
      features: [state.polygon.toGeoJSON()],
    })

    this.changeMode('simple_select', { featureIds: [state.polygon.id] })
  },

  onMouseMove(this: any, state: FreehandState, e: any) {
    // Handle click-to-draw mode (mouse move without dragging)
    if (!state.drawing || !state.polygon) return

    const zoom = this.map.getZoom()
    const stepPx = pxTolForZoom(zoom)

    const p = this.map.project(e.lngLat)
    const last = state.lastAddedPixel!
    const dist = Math.hypot(p.x - last.x, p.y - last.y)

    if (dist < stepPx) {
      // Update the live tail so the line tracks the mouse smoothly
      state.polygon.updateCoordinate(
        `0.${state.currentVertex + 1}`,
        e.lngLat.lng,
        e.lngLat.lat
      )
      return
    }

    // Append a new vertex
    state.currentVertex += 1
    state.polygon.updateCoordinate(
      `0.${state.currentVertex}`,
      e.lngLat.lng,
      e.lngLat.lat
    )
    state.lastAddedPixel = p
  },

  onMouseDown(this: any, state: FreehandState, e: any) {
    if (e.originalEvent && e.originalEvent.button !== 0) return // Only left click

    // Record position to decide later if this is a drag start or a click
    state.mouseDownPixel = this.map.project(e.lngLat)
    state.mouseDownLngLat = [e.lngLat.lng, e.lngLat.lat]

    // Take control from map panning immediately; we'll re-enable on finish/cancel
    disableMapInteractions(this)
  },

  onDrag(this: any, state: FreehandState, e: any) {
    const p = this.map.project(e.lngLat)

    // If not yet dragging or drawing, decide if movement is enough to start drag mode
    if (!state.dragging && !state.drawing) {
      if (!state.mouseDownPixel) return

      const moved = Math.hypot(
        p.x - state.mouseDownPixel.x,
        p.y - state.mouseDownPixel.y
      )
      if (moved < DRAG_START_PX) return

      // Begin polygon in drag mode at the original mousedown location
      const [lng0, lat0] = state.mouseDownLngLat!
      state.dragging = true
      disableMapInteractions(this)
      startFeatureAt(this, state, lng0, lat0)
      // fall through to append current point below
    }

    if (!state.dragging || !state.polygon) return

    const zoom = this.map.getZoom()
    const stepPx = pxTolForZoom(zoom)

    const last = state.lastAddedPixel!
    const dist = Math.hypot(p.x - last.x, p.y - last.y)

    if (dist < stepPx) {
      // Update live tail
      state.polygon.updateCoordinate(
        `0.${state.currentVertex + 1}`,
        e.lngLat.lng,
        e.lngLat.lat
      )
      return
    }

    // Append a new vertex
    state.currentVertex += 1
    state.polygon.updateCoordinate(
      `0.${state.currentVertex}`,
      e.lngLat.lng,
      e.lngLat.lat
    )
    state.lastAddedPixel = p
  },

  onMouseUp(this: any, state: FreehandState) {
    if (state.dragging && state.polygon) {
      // Finish drawing on mouse up (drag mode)
      this.finishDrawing(state)
      return
    }

    // If neither drag nor click mode is active, restore map interactions
    if (!state.drawing) {
      enableMapInteractions(this)
      state.mouseDownPixel = undefined
      state.mouseDownLngLat = undefined
    }
  },

  onDblClick(this: any, state: FreehandState, e: any) {
    // Prevent default double-click zoom behavior
    e.preventDefault()
    if (state.drawing && state.polygon) {
      this.finishClickDrawing(state)
    }
  },

  onStop(this: any, state: FreehandState) {
    this.updateUIClasses({ mouse: 'none' })
    this.activateUIButton() // clear active button
    if (!state.polygon || this.getFeature(state.polygon.id) === undefined)
      return
    // If user exited without finishing and ring has too few points, delete
    if (state.currentVertex < 2)
      this.deleteFeature([state.polygon.id], { silent: true })
    this.map.dragPan.enable()
    this.map.doubleClickZoom.enable()
  },

  toDisplayFeatures(
    this: any,
    state: FreehandState,
    geojson: any,
    display: (feat: any) => void
  ) {
    // Only display if we have a polygon and valid coordinates
    if (state.polygon && geojson.geometry.coordinates[0].length > 0) {
      display(geojson)
    }
  },

  onTrash(this: any, state: FreehandState) {
    if (state.polygon) {
      this.deleteFeature([state.polygon.id], { silent: true })
    }
    this.changeMode('simple_select')
  },

  onTap() {
    // Do nothing - we handle drawing with click
  },

  onKeyUp(this: any, state: FreehandState, e: any) {
    const key = e.key || ''
    const code = e.keyCode

    // Enter finishes current drawing (works for both click and drag modes)
    if (key === 'Enter' || code === 13) {
      e.preventDefault?.()
      if (state.drawing && state.polygon) {
        this.finishClickDrawing(state)
        return
      }
      if (state.dragging && state.polygon) {
        this.finishDrawing(state)
        return
      }
    }

    if (code === 27 || key === 'Escape') {
      // Escape key - cancel drawing
      if (state.polygon) {
        this.deleteFeature([state.polygon.id], { silent: true })
      }
      this.changeMode('simple_select')
    }
  },

  onTouchStart() {
    // Do nothing for now
  },

  onTouchMove() {
    // Do nothing for now
  },

  onTouchEnd() {
    // Do nothing for now
  },
}

export default DrawFreehandPolygon
